import React from "react";
import clsx from "clsx";
import { Dialog } from "@/components/Dialog";
import { UserRowModel } from "./SettingsUsersTable";
import { usePromoteUser } from "@/hooks/usePromoteUser";
import { customToast } from "@/utils/customToast";
import { RadioGroup, RadioGroupOption } from "@/components/RadioOption/RadioGroup";
import * as Sentry from "@sentry/react";

type RoleType = "admin" | "normal";

type EditRoleDialogProps = {
  show: boolean;
  onCancel: () => void;
  user: UserRowModel;
  onRoleChangeSuccess?: () => void;
};

export function EditRoleDialog({ show, onCancel, user, onRoleChangeSuccess }: EditRoleDialogProps) {
  const initialRole = user.is_root ? "admin" : "normal";
  const [selectedRole, setSelectedRole] = React.useState<RoleType>(initialRole);
  const promoteUser = usePromoteUser();

  const isRoleChanged = selectedRole !== initialRole;

  const roleOptions: RadioGroupOption<RoleType>[] = [
    {
      value: "admin",
      label: "Admin",
      icon: "Admin",
    },
    {
      value: "normal",
      label: "Normal User",
      icon: "NormalUser",
    },
  ];

  const handleSubmit = async () => {
    try {
      // NOTE: The user cannot be demoted to a normal user
      if (selectedRole === "admin" && !user.is_root) {
        await promoteUser.mutateAsync({ userId: user.id });
        customToast("User Role was changed Successfully.", "success", "bottom");
      } else {
        console.error("Invalid role change operation", {
          selectedRole,
          isUserRoot: user.is_root,
          userId: user.id,
        });

        Sentry.captureEvent({
          message: "Invalid role change operation attempted",
          level: "error",
          extra: {
            selectedRole,
            isUserRoot: user.is_root,
            userId: user.id,
            initialRole,
          },
        });

        customToast("Role change operation not permitted", "error", "bottom");
        return;
      }

      if (onRoleChangeSuccess) {
        onRoleChangeSuccess();
      } else {
        onCancel();
      }
    } catch (error) {
      console.error("Failed to update user privileges:", error);
      customToast("Failed to update user privileges", "error", "bottom");
    }
  };

  return (
    <Dialog
      show={show}
      message={
        <div className="p-2">
          <div className="mb-2 text-xs text-dark">Roles</div>
          <RadioGroup
            options={roleOptions}
            value={selectedRole}
            onChange={setSelectedRole}
            className="mb-6"
          />
          <button
            className={clsx(
              "w-full text-white py-2 px-4 rounded-lg transition",
              isRoleChanged && !promoteUser.isPending
                ? "bg-primaryColor hover:bg-primaryColor/90"
                : "bg-primaryColor/20 cursor-not-allowed",
            )}
            onClick={handleSubmit}
            disabled={!isRoleChanged || promoteUser.isPending}
          >
            {promoteUser.isPending ? "Loading..." : "Submit"}
          </button>
        </div>
      }
      showBorder={false}
      confirmButton={{ show: false }}
      onDismiss={onCancel}
    />
  );
}
